import { Link } from "react-router-dom";
import BrandWordmark from "./BrandWordmark";
import { useTranslation } from "./i18n";
import LanguageSwitcher from "./LanguageSwitcher";
import { DRIVE_SYNC_AVAILABLE } from "./lib/features";
import { DATA_PROCESSING_VERSION, legalConfig, legalConfigReady, PRIVACY_VERSION, TERMS_VERSION } from "./lib/legal";

type DocumentKind = "privacy" | "terms" | "data-processing";

function Contact() {
  const { t } = useTranslation("legalPolicy");
  return (
    <>
      <p>
        <b>{t("operator")}</b>
        {legalConfig.operatorName || t("missing")}
      </p>
      <p>
        <b>{t("contactName")}</b>
        {legalConfig.contactName || t("missing")}
      </p>
      <p>
        <b>{t("contactEmail")}</b>
        {legalConfig.contactEmail ? (
          <a href={`mailto:${legalConfig.contactEmail}`}>{legalConfig.contactEmail}</a>
        ) : (
          t("missing")
        )}
      </p>
    </>
  );
}

function PolicySections({ items }: { items: { title: string; paragraphs: string[]; id?: string }[] }) {
  return items.map(({ title, paragraphs, id }) => (
    <section key={title}>
      <h2 id={id}>{title}</h2>
      {paragraphs.map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
    </section>
  ));
}

function Privacy() {
  const { t } = useTranslation("legalPolicy");
  return (
    <>
      <aside className="legal-summary">
        <b>{t("privacy.summaryTitle")}</b>
        <p>{t("privacy.summary")}</p>
      </aside>
      <PolicySections items={[{ title: t("privacy.operatorTitle"), paragraphs: [t("privacy.operator")] }]} />
      <Contact />
      <PolicySections
        items={[
          { title: t("privacy.accountTitle"), paragraphs: [t("privacy.account")], id: "teacher-account-pics" },
          {
            title: t("privacy.localTitle"),
            paragraphs: [t("privacy.local"), t("privacy.demo")],
            id: "student-roster-pics",
          },
          { title: t("privacy.feedbackTitle"), paragraphs: [t("privacy.feedback")], id: "beta-feedback-pics" },
        ]}
      />
      {DRIVE_SYNC_AVAILABLE && (
        <PolicySections
          items={[
            {
              title: t("privacy.syncTitle"),
              paragraphs: [t("privacy.sync"), t("privacy.syncRetention"), t("privacy.syncLimitedUse")],
            },
          ]}
        />
      )}
      <PolicySections items={[{ title: t("privacy.networkTitle"), paragraphs: [t("privacy.network")] }]} />
      <table>
        <thead>
          <tr>
            <th>{t("privacy.providerName")}</th>
            <th>{t("privacy.providerUse")}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{t("privacy.googleName")}</td>
            <td>{t("privacy.google")}</td>
          </tr>
          <tr>
            <td>{t("privacy.supabaseName")}</td>
            <td>{t("privacy.supabase")}</td>
          </tr>
          <tr>
            <td>{t("privacy.cloudflareName")}</td>
            <td>{t("privacy.cloudflare")}</td>
          </tr>
        </tbody>
      </table>
      <p>{t("privacy.region", { region: legalConfig.primaryDataRegion || t("missing") })}</p>
      <PolicySections
        items={[
          {
            title: t("privacy.retentionTitle"),
            paragraphs: [
              t("privacy.retention"),
              t("privacy.externalBackup"),
              t("privacy.accountRetention"),
              t("privacy.feedbackRetention"),
              t("privacy.providerRetention"),
            ],
          },
          { title: t("privacy.accessTitle"), paragraphs: [t("privacy.access")] },
          { title: t("privacy.securityTitle"), paragraphs: [t("privacy.security")] },
          {
            title: t("privacy.requestsTitle"),
            paragraphs: [t("privacy.requests"), t("privacy.deletion"), t("privacy.incidents")],
          },
        ]}
      />
      <p className="legal-reference">
        {t("privacy.reference")}{" "}
        <a
          href="https://www.pcpd.org.hk/tc_chi/resources_centre/publications/files/GN_picspps_c.pdf"
          target="_blank"
          rel="noreferrer"
        >
          {t("privacy.referenceLink")}
        </a>
        {" · "}
        <a href="https://law.pdpc.gov.tw/LawContent.aspx?id=FL010627" target="_blank" rel="noreferrer">
          {t("privacy.taiwanReference")}
        </a>
        {" · "}
        <a href="https://www.al.gov.mo/file/colect/col_lei-08/cn/1.htm" target="_blank" rel="noreferrer">
          {t("privacy.macauReference")}
        </a>
      </p>
    </>
  );
}

function Terms() {
  const { t } = useTranslation("legalPolicy");
  return (
    <>
      <PolicySections
        items={[
          {
            title: t("terms.scopeTitle"),
            paragraphs: [t("terms.scope", { operator: legalConfig.operatorName || t("missing") }), t("terms.adoption")],
          },
          { title: t("terms.supportTitle"), paragraphs: [t("terms.support")] },
          { title: t("terms.freeTitle"), paragraphs: [t("terms.free"), t("terms.unpaid")] },
          { title: t("terms.dataTitle"), paragraphs: [t("terms.data"), t("terms.restore")] },
          { title: t("terms.conductTitle"), paragraphs: [t("terms.conduct")] },
          { title: t("terms.connectionTitle"), paragraphs: [t("terms.connection")] },
          { title: t("terms.changesTitle"), paragraphs: [t("terms.changes"), t("terms.law")] },
        ]}
      />
      <h2>{t("terms.contactTitle")}</h2>
      <Contact />
    </>
  );
}

function DataProcessing() {
  const { t } = useTranslation("legalPolicy");
  return (
    <>
      <PolicySections
        items={[
          { title: t("processing.scopeTitle"), paragraphs: [t("processing.scope")] },
          {
            title: t("processing.responsibilitiesTitle"),
            paragraphs: [t("processing.responsibilities"), t("processing.providerResponsibilities")],
          },
          { title: t("processing.onlineTitle"), paragraphs: [t("processing.online")] },
          { title: t("processing.backupTitle"), paragraphs: [t("processing.backup")] },
          { title: t("processing.assistanceTitle"), paragraphs: [t("processing.assistance")] },
          { title: t("processing.endingTitle"), paragraphs: [t("processing.ending")] },
          { title: t("processing.futureTitle"), paragraphs: [t("processing.future")] },
        ]}
      />
      <h2>{t("processing.contactTitle")}</h2>
      <Contact />
    </>
  );
}

export default function LegalPage({ kind }: { kind: DocumentKind }) {
  const { t } = useTranslation("legalPolicy");
  const title = kind === "privacy" ? t("privacy.title") : kind === "terms" ? t("terms.title") : t("processing.title");
  const version = kind === "privacy" ? PRIVACY_VERSION : kind === "terms" ? TERMS_VERSION : DATA_PROCESSING_VERSION;
  return (
    <main className="legal-shell" id="main-content" aria-labelledby="legal-document-title" tabIndex={-1}>
      <a className="skip-link" href="#main-content">
        {t("skip")}
      </a>
      <header>
        <Link to="/" className="brand-home">
          <BrandWordmark />
        </Link>
        <nav aria-label={t("navigation")}>
          <Link to="/privacy">{t("privacyLink")}</Link>
          <Link to="/terms">{t("termsLink")}</Link>
          <Link to="/data-processing">{t("dataProcessingLink")}</Link>
          <Link to="/support">{t("supportLink")}</Link>
        </nav>
        <LanguageSwitcher />
      </header>
      <article className="legal-document">
        {!legalConfigReady && (
          <div className="legal-config-warning" role="alert">
            <b>{t("setupTitle")}</b>
            <p>{t("setupDescription")}</p>
          </div>
        )}
        <h1 id="legal-document-title">{title}</h1>
        <p className="legal-version">{t("version", { version })}</p>
        {kind === "privacy" ? <Privacy /> : kind === "terms" ? <Terms /> : <DataProcessing />}
        <p>
          <Link to="/support">{t("supportLink")}</Link>
        </p>
      </article>
    </main>
  );
}
